/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import {
  Component, EventEmitter, Input, Output,
} from '@angular/core';

import {
  UppyAllowedFileTypes,
  UppyComponentEvent,
} from '../types';
import { UppyDashboardService } from '../uppy-dashboard.service';

/**
 * This component does the following:
 *   Finishes upload flow with API call to our server when companion is done uploading to S3.
 *   Emits events on Upload Success, Upload Failure, and Complete.
 */
@Component({
  selector: 'bulb-uppy-dashboard',
  template: '',
  providers: [
    // Provide this component with its own instance of UppyCoreService.
    // If we use the ngModule's (singleton) instance, it will be shared with other components,
    // and we will receive events from other components that use the same singleton.
    UppyDashboardService,
  ],
  standalone: false,
})
export class UppyDashboardComponent {

  /**
   * Optional input of an array of allowed files types.
   */
  @Input() allowedFileType?: UppyAllowedFileTypes[] = [UppyAllowedFileTypes.ANY];

  /**
   * Optional input of the folder id the asset should be uploaded to.
   */
  @Input() folderId?: string;

  /**
   * If multiple uploads are allowed. Defaults to false.
   */
  @Input() multiSelect: boolean = false;

  /**
   * When true, the Asset Library plugin is loaded.
   * We don't want this to show up when we are uploading assets to the Asset Library.
   */
  @Input() showAssetLibrary: boolean = false;

  /**
   * When true, the embed plugin is loaded.
   */
  @Input() showEmbed: boolean = false;

  /**
   * When true, the Adobe Express plugin is loaded.
   */
  @Input() useAdobeExpressPlugin: boolean = true;

  /**
   * When true, the Canva Connect plugin is loaded.
   */
  @Input() useCanvaPlugin: boolean = true;

  /**
   * EventEmitter for OnSuccess, OnFailure, and Complete events.
   */
  @Output() uppyEventEmitter: EventEmitter<UppyComponentEvent>;

  constructor(
    private uppyDashboardSvc: UppyDashboardService,
  ) {
    this.uppyEventEmitter = this.uppyDashboardSvc.uppyEventEmitter;
  }

  /**
   * Opens the Uppy Dashboard modal.
   */
  public async open(): Promise<void> {
    return this.uppyDashboardSvc.open({
      multiSelect: this.multiSelect,
      allowedFileTypes: this.allowedFileType,
      assetLibrary: this.showAssetLibrary,
      folderId: this.folderId,
      embed: this.showEmbed,
      useAdobeExpressPlugin: this.useAdobeExpressPlugin,
      useCanvaPlugin: this.useCanvaPlugin,
    });
  }
}
